import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { stepUp } from "@/lib/passkey";
import { setStepUpToken } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Fingerprint, ShieldCheck, LogOut, QrCode } from "lucide-react";
import { toast } from "sonner";
import BusinessCardModal from "@/components/BusinessCardModal";

const THRESHOLD_MS = 10 * 1000; // lock after being in the background for >10s
const HIDDEN_KEY = "renova_hidden_at";

export default function AppLock() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [locked, setLocked] = useState(false);
  const [unlocking, setUnlocking] = useState(false);
  const [cardOpen, setCardOpen] = useState(false);

  const hasPasskey = !!user?.has_passkey;

  useEffect(() => {
    if (!hasPasskey) return;

    // Record WHEN we went to the background. Only write once per background
    // episode so the first "leave" is the one that counts (and so nothing ever
    // overwrites the stale timestamp on resume).
    const markHidden = () => {
      if (localStorage.getItem(HIDDEN_KEY) == null) {
        localStorage.setItem(HIDDEN_KEY, String(Date.now()));
      }
    };

    // On return to the foreground (or on a fresh cold relaunch), lock if we were
    // away longer than the threshold. Never writes "now" — only reads.
    const evaluate = () => {
      if (document.visibilityState !== "visible") return;
      const raw = localStorage.getItem(HIDDEN_KEY);
      if (raw == null) return;
      const hiddenAt = parseInt(raw, 10);
      const away = Date.now() - hiddenAt;
      if (!Number.isNaN(hiddenAt) && away > THRESHOLD_MS) {
        setLocked(true);
      } else {
        localStorage.removeItem(HIDDEN_KEY); // short absence — reset, no lock
      }
    };

    const onVisibility = () => {
      if (document.visibilityState === "hidden") markHidden();
      else evaluate();
    };

    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("pagehide", markHidden);
    window.addEventListener("pageshow", evaluate);
    window.addEventListener("focus", evaluate);
    window.addEventListener("online", evaluate);

    // Cold relaunch: the app was killed while backgrounded — HIDDEN_KEY still
    // holds the moment we left, so evaluate() on mount will lock.
    evaluate();

    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("pagehide", markHidden);
      window.removeEventListener("pageshow", evaluate);
      window.removeEventListener("focus", evaluate);
      window.removeEventListener("online", evaluate);
    };
  }, [hasPasskey]);

  const unlock = async () => {
    setUnlocking(true);
    try {
      const t = await stepUp();
      setStepUpToken(t);
      localStorage.removeItem(HIDDEN_KEY);
      setLocked(false);
    } catch (e) {
      if (e?.name === "NotAllowedError") toast.error("Verification cancelled or timed out.");
      else toast.error("Could not verify passkey. Try again.");
    } finally {
      setUnlocking(false);
    }
  };

  const doLogout = () => { logout(); navigate("/login"); };

  if (!locked || !hasPasskey) return null;

  return (
    <div className="fixed inset-0 z-[100] bg-[#0A0A0A]/95 backdrop-blur-md flex items-center justify-center p-6"
      data-testid="app-lock-overlay">
      <div className="w-full max-w-sm text-center">
        <div className="w-16 h-16 rounded-2xl bg-[#1B4332] flex items-center justify-center mx-auto mb-5">
          <ShieldCheck className="w-8 h-8 text-white" />
        </div>
        <h2 className="font-heading text-2xl font-extrabold text-white tracking-tight">Locked for your security</h2>
        <p className="text-sm text-white/60 mt-2 mb-7">
          You were away for a while. Verify with your passkey to continue — you stay signed in.
        </p>
        <Button onClick={unlock} disabled={unlocking} data-testid="app-lock-unlock-btn"
          className="w-full h-12 bg-white text-[#0A0A0A] hover:bg-white/90 font-semibold">
          <Fingerprint className="w-5 h-5 mr-2" /> {unlocking ? "Verifying…" : "Unlock with passkey"}
        </Button>
        <button onClick={doLogout} data-testid="app-lock-logout-btn"
          className="w-full mt-3 h-10 flex items-center justify-center gap-2 text-sm text-white/50 hover:text-white/80">
          <LogOut className="w-4 h-4" /> Sign out instead
        </button>

        <div className="mt-6 pt-5 border-t border-white/10">
          <button onClick={() => setCardOpen(true)} data-testid="app-lock-show-card-btn"
            className="w-full h-10 flex items-center justify-center gap-2 text-sm text-white/70 hover:text-white">
            <QrCode className="w-4 h-4" /> Show my business card
          </button>
        </div>
      </div>

      <BusinessCardModal open={cardOpen} onOpenChange={setCardOpen}
        name={user?.name} email={user?.email} role={user?.role} profile={user?.profile || {}} contentClassName="z-[130]" />
    </div>
  );
}
